import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiFetch } from "../../api/api";
import { useOrg } from "../orgs/org-context";
import PrintAccounting from '../printManager/PrintAccounting';
import { printSelectedRecords, admissionMessage } from '../printManager/printAccountingApi';
import { useRef } from 'react';
import { toast } from 'react-toastify';
import RecordDrawer from "./RecordDrawer";
import "./DataDocs.css";
import { confirmDialog } from '../../components/EveryIdDialogHost';

export default function DataDocs() {
  const { datasetId } = useParams();
  const navigate = useNavigate();
  const { activeOrgId, activeOrg } = useOrg();

  const printInFlight = useRef(false);
  const [printResults, setPrintResults] = useState([]);
  const [printRevision, setPrintRevision] = useState(0);
  const [printBusy, setPrintBusy] = useState(false);
  const [schema, setSchema] = useState(null);
  const [records, setRecords] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const currentRecordIndex =
  records.findIndex(
    r =>
      String(r._id) ===
      String(
        selectedRecord?._id
      )
  );
  const orgId =
    activeOrgId ||
    activeOrg?._id ||
    localStorage.getItem("activeOrgId") ||
    "none";

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => { setPrintResults([]); }, [datasetId, orgId]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);

    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    loadSchema();
  }, [datasetId, orgId]);

  useEffect(() => {
    loadRecords();
  }, [datasetId, orgId, page, pageSize, debouncedSearch]);

  const loadSchema = async () => {
    try {
      const data = await apiFetch(`/datasets/schema/${datasetId}`, {
        method: "GET",
      });
      console.log("schema:", data)
      setSchema(data);
    } catch (err) {
      console.error("Failed loading dataset schema", err);
    }
  };

  const handleExportSelected = () => {

    const selectedRecords =
      records.filter(r =>
        selectedIds.includes(r._id)
      );

    if (!selectedRecords.length) {
      return;
    }

    const systemHeaders = [
      '_id',
      'displayName',
      'photoUrl',
      'outputUrl',
      'renderStatus',
      'createdAt',
      'updatedAt'
    ];

    const fieldHeaders =
      schema.fields.map(
        f => f.label
      );

    const headers = [
      ...systemHeaders,
      ...fieldHeaders
    ];

    const rows =
      selectedRecords.map(record => {

        const systemValues = [

          record._id || '',

          record.displayName || '',

          record.photoUrl || '',

          record.outputUrl || '',

          record.renderStatus || '',

          record.createdAt
            ? new Date(record.createdAt)
                .toISOString()
            : '',

          record.updatedAt
            ? new Date(record.updatedAt)
                .toISOString()
            : ''

        ];

        const fieldValues =
          schema.fields.map(field =>
            record.fields?.[
              field.key
            ] ?? ''
          );

        return [
          ...systemValues,
          ...fieldValues
        ];

      });

    const csv = [

      headers.join(','),

      ...rows.map(row =>
        row.map(value => {

          const text =
            String(value ?? '');

          return `"${text.replace(/"/g, '""')}"`;

        }).join(',')
      )

    ].join('\n');

    const blob = new Blob(
      [csv],
      {
        type:
          'text/csv;charset=utf-8;'
      }
    );

    const url =
      window.URL.createObjectURL(
        blob
      );

    const link =
      document.createElement('a');

    link.href = url;

    link.download =
      `${schema?.name || 'records'}_${new Date()
        .toISOString()
        .slice(0,10)}.csv`;

    link.click();

    window.URL.revokeObjectURL(
      url
    );

    toast.success(
      `${selectedRecords.length} record(s) exported`
    );

  };

  const handleReprocessAll = async () => {

    const testLimit = 10;

    const label =
      debouncedSearch
        ? `up to ${testLimit} matching records for "${debouncedSearch}"`
        : `up to ${testLimit} records in this dataset`;

    const ok =
      await confirmDialog(
        `Reprocess ${label}? This will rebuild images from the original photos.`,
        { confirmLabel: 'Reprocess' }
      );

    if (!ok) {
      return;
    }

    try {

      const result =
        await toast.promise(

          apiFetch('/datadoc/reprocessAll', {
            method: 'POST',
            body: {
              datasetId,
              search: debouncedSearch,
              // limit: testLimit
            }
          }),

          {
            pending: 'Queueing reprocess jobs...',
            success: 'Reprocess jobs queued.',
            error: 'Reprocess all failed.'
          }

        );

      console.log(
        'reprocessAll result:',
        result
      );

      await loadRecords();

    }
    catch (err) {

      console.error(
        'Reprocess all failed',
        err
      );

    }

  };

  const handlePrintSelected = async () => {
    if (printInFlight.current) return;
    printInFlight.current = true;
    setPrintBusy(true);
    try {
      const results = await printSelectedRecords(selectedIds, datasetId);
      setPrintResults(results);
      setPrintRevision(v => v + 1);
      setSelectedIds([]);
    } finally { printInFlight.current = false; setPrintBusy(false); }
  };


  const loadRecords = async () => {
    try {
      setLoading(true);

      const data = await apiFetch(`/datadoc/list/${datasetId}`, {
        method: "POST",
        body: {
          cnt: page * pageSize,
          lmt: pageSize,
          search: debouncedSearch,
        },
      });
      console.log("data:", data)
      setRecords(data?.items || []);
      setTotal(Number(data?.total || 0));
      setSelectedIds([]);
    } catch (err) {
      console.error("Failed loading data records", err);
      setRecords([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const visibleFields = useMemo(() => {
    return Array.isArray(schema?.fields) ? schema.fields : [];
  }, [schema]);

  const allVisibleSelected =
    records.length > 0 &&
    records.every((r) => selectedIds.includes(String(r._id)));

  const toggleOne = (id) => {
    const sid = String(id);

    setSelectedIds((prev) =>
      prev.includes(sid)
        ? prev.filter((x) => x !== sid)
        : [...prev, sid]
    );
  };

  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds([]);
      return;
    }

    setSelectedIds(records.map((r) => String(r._id)));
  };

  const getDisplayName = (row) => {
    if (row.displayName) return row.displayName;

    const f = row.fields || {};
    const first =
      f.firstName ||
      f.firstname ||
      f.FirstName ||
      "";

    const last =
      f.lastName ||
      f.lastname ||
      f.LastName ||
      "";

    const full = `${first} ${last}`.trim();

    return full || row._id;
  };

  return (
    <div className="data-records-page">
      <div className="data-records-header">
        <div>
          <h2>{schema?.name || "Data Records"}</h2>

          <div className="data-records-subtitle">
            {total} record{total === 1 ? "" : "s"}
          </div>
        </div>

        <button
          className="btn-gray"
          onClick={() => navigate("/datasets/datadoclist")}
        >
          Back to Data Records
        </button>
      </div>

      <div className="data-records-toolbar">
        <input
          className="data-records-search"
          type="text"
          placeholder="Search records..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button
          className="btn-gray"
          onClick={() => {
            setSearch('');
            setPage(0);
          }}
        >
          Reset
        </button>
        <div className="data-records-selected">
          Selected: {selectedIds.length}
        </div>

        <button
          className="btn-primary"
          disabled={printBusy || selectedIds.length === 0}
          onClick={handlePrintSelected}
        >
          Print Selected
        </button>
        <button
          className="btn-gray"
          disabled={selectedIds.length === 0}
          onClick={handleExportSelected}
        >
          Export Selected
        </button>

        <button
          className="btn-gray"
          disabled={loading || total === 0}
          onClick={handleReprocessAll}
          title={
            debouncedSearch
              ? 'Reprocess all records matching the current search'
              : 'Reprocess all records in this dataset'
          }
        >
          Reprocess All
        </button>

        <button className="btn-gray" disabled>
          Columns
        </button>
      </div>

      <div className="data-records-table-shell">
        <div className="data-records-scroll">
          <table className="data-records-table">
            <thead>
              <tr>
                <th className="sticky-col select-col">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                  />
                </th>

                <th className="sticky-col photo-col sticky-photo">
                  Photo
                </th>
                <th className="sticky-col action-col sticky-action">
                  Actions
                </th>
                {visibleFields.map((field) => (
                  <th key={field.key}>
                    {field.label || field.key}
                  </th>
                ))}

                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={visibleFields.length + 6}>
                    Loading...
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={visibleFields.length + 6}>
                    No records found.
                  </td>
                </tr>
              ) : (
                records.map((row) => {
                  const rowId = String(row._id);

                  return (
                    <tr key={rowId}>
                      <td className="sticky-col select-col">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(rowId)}
                          onChange={() => toggleOne(rowId)}
                        />
                      </td>

                      <td className="sticky-col photo-col sticky-photo">
                        {row.photoUrl ? (
                          <img
                            className="data-record-thumb"
                            src={row.photoUrl}
                            alt=""
                            onClick={() =>
                              setPhotoPreview({
                                url: row.photoUrl,
                                name: getDisplayName(row),
                              })
                            }
                          />
                        ) : (
                          <span className="no-photo">—</span>
                        )}
                      </td>
                      <td className="sticky-col action-col sticky-action">
                        <button
                          className="icon-btn"
                          title="View"
                          onClick={() => setSelectedRecord(row)}
                        >
                          👁
                        </button>

                        <button
                          className="icon-btn"
                          title="Print"
                        >
                          🖨
                        </button>

                      </td>

                      {visibleFields.map((field) => (
                        <td key={field.key}>
                          {row.fields?.[field.key] ?? ""}
                        </td>
                      ))}

                      <td>
                        <span className={`status-pill status-${row.renderStatus || "unknown"}`}>
                          {row.renderStatus || "unknown"}
                        </span>
                      </td>

                      <td>
                        {row.createdAt
                          ? new Date(row.createdAt).toLocaleString()
                          : ""}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="data-records-footer">
        <div>
          Rows per page:&nbsp;
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(0);
            }}
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
        </div>

        <div className="data-records-pager">
          <button
            onClick={() => setPage(0)}
            disabled={page === 0}
          >
            First
          </button>

          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            Previous
          </button>

          <span>
            Page {total === 0 ? 0 : page + 1} of {total === 0 ? 0 : totalPages}
          </span>

          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page + 1 >= totalPages}
          >
            Next
          </button>

          <button
            onClick={() => setPage(totalPages - 1)}
            disabled={page + 1 >= totalPages}
          >
            Last
          </button>
        </div>
      </div>
      {printBusy && <p role="status">Sending print requests...</p>}
      {printResults.length > 0 && <button type="button" onClick={() => setPrintResults([])}>Dismiss print results</button>}
      {printResults.map((result, index) => <div key={result.dataDocId}>
          <strong>Selected record {index + 1}</strong>
          <p role="status">{admissionMessage(result)}</p>
          <PrintAccounting dataDocId={result.dataDocId} revision={printRevision} autoRefresh={false}
            uncertainJobId={result.admissionState === 'uncertain' ? result.jobId : undefined} />
        </div>)}
      {selectedRecord && (
        <RecordDrawer
          record={selectedRecord}
          schema={schema}
          records={records}
          currentIndex={
            currentRecordIndex
          }
          onSelectRecord={
            setSelectedRecord
          }
          onClose={() =>
            setSelectedRecord(null)
          }
          onSaved={loadRecords}
        />

      )}
      {photoPreview && (
        <div
          className="photo-preview-overlay"
          onClick={() => setPhotoPreview(null)}
        >
          <div
            className="photo-preview-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="photo-preview-header">
              <strong>{photoPreview.name}</strong>

              <button
                className="btn-gray"
                onClick={() => setPhotoPreview(null)}
              >
                Close
              </button>
            </div>

            <img
              src={photoPreview.url}
              alt={photoPreview.name}
            />
          </div>
        </div>
      )}
    </div>
  );
}
